/**
 * Copyright 2025 Lumavec Group LLC
 *
 * ConflictResolutionDialog - Modal dialog for resolving merge conflicts
 */

import React, { useState, useCallback, useEffect } from 'react';
import { X, GitMerge, AlertTriangle, Check, Loader, FileText, CheckCircle } from 'lucide-react';
import { ConflictResolutionPanel } from './ConflictResolutionPanel';
import { ThreeWayDiffViewer } from './ThreeWayDiffViewer';
import {
  ConflictInfo,
  ConflictResolution,
  ApplyResolutionsResult
} from './types';
import {
  resolveConflict as resolveConflictApi,
  batchResolveConflicts as batchResolveConflictsApi,
  applyResolutions as applyResolutionsApi,
} from '../../lib/api';

interface ConflictResolutionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  conflicts: ConflictInfo[];
  repositoryUrl: string;
  sourceControlProvider: 'github' | 'gitlab';
  onAllResolved?: (resolutions: ConflictResolution[]) => void;
  autoApplyThreshold?: number;
}

export function ConflictResolutionDialog({
  isOpen,
  onClose,
  conflicts: initialConflicts,
  repositoryUrl,
  sourceControlProvider,
  onAllResolved,
  autoApplyThreshold = 0.8
}: ConflictResolutionDialogProps) {
  // State
  const [conflictResolutions, setConflictResolutions] = useState<ConflictResolution[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isResolvingAll, setIsResolvingAll] = useState(false);
  const [isResolvingSingle, setIsResolvingSingle] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<ApplyResolutionsResult | null>(null);

  // Initialize conflict resolutions from props
  useEffect(() => {
    if (isOpen && initialConflicts.length > 0) {
      setConflictResolutions(
        initialConflicts.map(conflict => ({
          conflict,
          status: 'pending' as const
        }))
      );
      setSelectedIndex(0);
    }
  }, [isOpen, initialConflicts]);

  // API call to resolve a single conflict
  const resolveConflictWithAI = useCallback(async (
    conflict: ConflictInfo
  ) => {
    const result = await resolveConflictApi(
      {
        filePath: conflict.filePath,
        entityId: conflict.entityId,
        entityType: conflict.entityType,
        documentType: conflict.documentType,
        baseVersion: conflict.baseVersion,
        localVersion: conflict.localVersion,
        remoteVersion: conflict.remoteVersion
      },
      repositoryUrl,
      sourceControlProvider
    );

    if (!result.success) {
      throw new Error(result.error || 'Failed to resolve conflict');
    }

    return result;
  }, [repositoryUrl, sourceControlProvider]);

  // API call to batch resolve conflicts
  const batchResolveConflicts = useCallback(async (
    conflicts: ConflictInfo[]
  ) => {
    const result = await batchResolveConflictsApi(
      conflicts.map(c => ({
        filePath: c.filePath,
        entityId: c.entityId,
        entityType: c.entityType,
        documentType: c.documentType,
        baseVersion: c.baseVersion,
        localVersion: c.localVersion,
        remoteVersion: c.remoteVersion
      })),
      autoApplyThreshold,
      sourceControlProvider
    );

    if (!result.success && result.summary.failed === conflicts.length) {
      throw new Error('Failed to batch resolve conflicts');
    }

    return result;
  }, [sourceControlProvider, autoApplyThreshold]);

  // Handle resolve all with AI
  const handleResolveAllWithAI = useCallback(async () => {
    const pendingConflicts = conflictResolutions
      .filter(cr => cr.status === 'pending')
      .map(cr => cr.conflict);

    if (pendingConflicts.length === 0) return;

    setIsResolvingAll(true);

    // Mark all pending as resolving
    setConflictResolutions(prev => prev.map(cr =>
      cr.status === 'pending' ? { ...cr, status: 'resolving' as const } : cr
    ));

    try {
      const result = await batchResolveConflicts(pendingConflicts);

      // Update resolutions based on result
      setConflictResolutions(prev => prev.map(cr => {
        if (cr.status !== 'resolving') return cr;

        const resolved = result.resolved.find(r => r.entityId === cr.conflict.entityId);
        const failed = result.failed.find(f => f.entityId === cr.conflict.entityId);

        if (resolved) {
          return {
            ...cr,
            status: resolved.autoApplied ? 'resolved' as const : 'pending' as const,
            method: resolved.autoApplied ? 'ai' as const : undefined,
            aiResult: resolved.result,
            resolvedContent: resolved.autoApplied && resolved.result.resolution
              ? JSON.stringify(resolved.result.resolution, null, 2)
              : undefined
          };
        }

        if (failed) {
          return {
            ...cr,
            status: 'error' as const,
            error: failed.error
          };
        }

        return { ...cr, status: 'pending' as const };
      }));

    } catch (error: any) {
      // Mark all resolving as error
      setConflictResolutions(prev => prev.map(cr =>
        cr.status === 'resolving'
          ? { ...cr, status: 'error' as const, error: error.message }
          : cr
      ));
    } finally {
      setIsResolvingAll(false);
    }
  }, [conflictResolutions, batchResolveConflicts]);

  // Handle requesting AI for a single conflict
  const handleRequestAI = useCallback(async () => {
    if (selectedIndex === null) return;

    const conflict = conflictResolutions[selectedIndex];
    if (!conflict || conflict.aiResult) return;

    setIsResolvingSingle(true);

    setConflictResolutions(prev => prev.map((cr, idx) =>
      idx === selectedIndex ? { ...cr, status: 'resolving' as const } : cr
    ));

    try {
      const result = await resolveConflictWithAI(conflict.conflict);

      setConflictResolutions(prev => prev.map((cr, idx) =>
        idx === selectedIndex
          ? {
              ...cr,
              status: 'pending' as const,
              aiResult: result
            }
          : cr
      ));
    } catch (error: any) {
      setConflictResolutions(prev => prev.map((cr, idx) =>
        idx === selectedIndex
          ? { ...cr, status: 'error' as const, error: error.message }
          : cr
      ));
    } finally {
      setIsResolvingSingle(false);
    }
  }, [selectedIndex, conflictResolutions, resolveConflictWithAI]);

  // Resolution handlers
  const handleAcceptLocal = useCallback(() => {
    if (selectedIndex === null) return;
    
    setConflictResolutions(prev => prev.map((cr, idx) =>
      idx === selectedIndex
        ? {
            ...cr,
            status: 'resolved' as const,
            method: 'local' as const,
            resolvedContent: cr.conflict.localVersion
          }
        : cr
    ));
  }, [selectedIndex]);

  const handleAcceptRemote = useCallback(() => {
    if (selectedIndex === null) return;
    
    setConflictResolutions(prev => prev.map((cr, idx) =>
      idx === selectedIndex
        ? {
            ...cr,
            status: 'resolved' as const,
            method: 'remote' as const,
            resolvedContent: cr.conflict.remoteVersion
          }
        : cr
    ));
  }, [selectedIndex]);

  const handleAcceptAI = useCallback(() => {
    if (selectedIndex === null) return;

    const conflict = conflictResolutions[selectedIndex];
    if (!conflict?.aiResult?.resolution) return;

    setConflictResolutions(prev => prev.map((cr, idx) =>
      idx === selectedIndex
        ? {
            ...cr,
            status: 'resolved' as const,
            method: 'ai' as const,
            resolvedContent: JSON.stringify(cr.aiResult!.resolution, null, 2)
          }
        : cr
    ));
  }, [selectedIndex, conflictResolutions]);

  const handleSaveManual = useCallback((content: string) => {
    if (selectedIndex === null) return;

    setConflictResolutions(prev => prev.map((cr, idx) =>
      idx === selectedIndex
        ? {
            ...cr,
            status: 'resolved' as const,
            method: 'manual' as const,
            resolvedContent: content
          }
        : cr
    ));
  }, [selectedIndex]);

  const handleDefer = useCallback(() => {
    if (selectedIndex === null) return;

    setConflictResolutions(prev => prev.map((cr, idx) =>
      idx === selectedIndex
        ? { ...cr, status: 'deferred' as const }
        : cr
    ));
  }, [selectedIndex]);

  // Apply resolutions to files
  const handleApplyResolutions = useCallback(async () => {
    const resolvedConflicts = conflictResolutions.filter(
      cr => cr.status === 'resolved' && cr.resolvedContent
    );

    if (resolvedConflicts.length === 0) return;

    setIsApplying(true);
    setApplyResult(null);

    try {
      const result = await applyResolutionsApi(
        repositoryUrl,
        resolvedConflicts.map(cr => ({
          filePath: cr.conflict.filePath,
          entityId: cr.conflict.entityId,
          entityType: cr.conflict.entityType,
          documentType: cr.conflict.documentType,
          resolvedContent: cr.resolvedContent!,
          method: cr.method!
        })),
        true, // autoCommit
        `Resolve ${resolvedConflicts.length} merge conflict(s)`,
        sourceControlProvider
      );

      setApplyResult(result);

      if (result.success) {
        // Mark as successfully applied
        console.log(`[${new Date().toISOString()}] Applied resolutions: ${result.filesModified.length} files modified`);
      }

    } catch (error: any) {
      console.error('Error applying resolutions:', error);
      setApplyResult({
        success: false,
        filesModified: [],
        filesStaged: [],
        errors: [{ filePath: '', entityId: '', error: error.message }]
      });
    } finally {
      setIsApplying(false);
    }
  }, [conflictResolutions, repositoryUrl, sourceControlProvider]);

  // Check if all resolved
  const allResolved = conflictResolutions.every(
    cr => cr.status === 'resolved' || cr.status === 'deferred'
  );
  
  // Count resolved (not deferred)
  const resolvedCount = conflictResolutions.filter(cr => cr.status === 'resolved').length;

  // Handle close with callback
  const handleClose = useCallback(() => {
    if (applyResult?.success && onAllResolved) {
      onAllResolved(conflictResolutions);
    }
    // Reset state
    setApplyResult(null);
    onClose();
  }, [applyResult, onAllResolved, conflictResolutions, onClose]);

  if (!isOpen) return null;

  const selectedConflict = selectedIndex !== null ? conflictResolutions[selectedIndex] : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-[95vw] max-w-[1400px] h-[90vh] max-h-[800px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 bg-orange-50 border-b border-orange-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <GitMerge size={20} className="text-orange-600" />
            <div>
              <h2 className="font-semibold text-gray-900">Resolve Merge Conflicts</h2>
              <p className="text-xs text-gray-600">
                {conflictResolutions.filter(c => c.status === 'pending').length} pending •{' '}
                {conflictResolutions.filter(c => c.status === 'resolved').length} resolved
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-orange-100 rounded-full"
          >
            <X size={18} className="text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left sidebar - conflict list */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            <ConflictResolutionPanel
              conflicts={conflictResolutions}
              selectedIndex={selectedIndex}
              onSelectConflict={setSelectedIndex}
              onResolveAllWithAI={handleResolveAllWithAI}
              isResolvingAll={isResolvingAll}
              autoApplyThreshold={autoApplyThreshold}
            />
          </div>

          {/* Right panel - diff viewer */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedConflict ? (
              <ThreeWayDiffViewer
                conflict={selectedConflict}
                onAcceptLocal={handleAcceptLocal}
                onAcceptRemote={handleAcceptRemote}
                onAcceptAI={handleAcceptAI}
                onRequestAI={handleRequestAI}
                onSaveManual={handleSaveManual}
                onDefer={handleDefer}
                onClose={() => setSelectedIndex(null)}
                isResolvingAI={isResolvingSingle}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <AlertTriangle size={48} className="mx-auto mb-4 text-orange-300" />
                  <p>Select a conflict to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
          {/* Apply Result Feedback */}
          {applyResult && (
            <div className={`mb-3 p-3 rounded-lg ${
              applyResult.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              {applyResult.success ? (
                <div className="flex items-start gap-2">
                  <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Resolutions Applied Successfully
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      {applyResult.filesModified.length} file(s) modified and staged
                      {applyResult.commitHash && (
                        <span className="ml-2 font-mono">
                          ({applyResult.commitHash.substring(0, 7)})
                        </span>
                      )}
                    </p>
                    {applyResult.filesModified.length > 0 && (
                      <ul className="mt-2 text-xs text-green-700">
                        {applyResult.filesModified.map(file => (
                          <li key={file} className="flex items-center gap-1">
                            <FileText size={12} />
                            {file}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <AlertTriangle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800">
                      Failed to Apply Some Resolutions
                    </p>
                    {applyResult.errors.length > 0 && (
                      <ul className="mt-1 text-xs text-red-700">
                        {applyResult.errors.map((err, idx) => (
                          <li key={idx}>
                            {err.filePath ? `${err.filePath}: ` : ''}{err.error}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {applyResult?.success ? (
                <span className="flex items-center gap-2 text-green-600">
                  <Check size={16} />
                  Changes saved to repository
                </span>
              ) : allResolved ? (
                <span className="flex items-center gap-2 text-green-600">
                  <Check size={16} />
                  All conflicts handled ({resolvedCount} resolved, {conflictResolutions.length - resolvedCount} deferred)
                </span>
              ) : (
                <span>
                  {resolvedCount} of {conflictResolutions.length} resolved
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="btn-secondary px-4 py-2"
                disabled={isApplying}
              >
                {applyResult?.success ? 'Done' : 'Cancel'}
              </button>
              {!applyResult?.success && resolvedCount > 0 && (
                <button
                  onClick={handleApplyResolutions}
                  disabled={isApplying || resolvedCount === 0}
                  className="btn-primary px-4 py-2 flex items-center gap-2"
                >
                  {isApplying ? (
                    <>
                      <Loader size={14} className="animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      Apply {resolvedCount} Resolution{resolvedCount !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
